/**
 * DAG View Component
 *
 * Displays tasks as a directed acyclic graph laid out by dependency depth.
 */

import { useMemo, useState, useRef, useEffect, useCallback } from "react";
import type { Task } from "../lib/usePlanReasoning";
import Avatar from "./Avatar";

interface DAGViewProps {
  tasks: Task[];
  taskDeps: Map<string, string[]>;
  isLoading?: boolean;
  onTaskClick?: (taskId: string) => void;
  selectedTask?: string | null;
}

interface Position {
  x: number;
  y: number;
}

interface LayoutResult {
  positions: Map<string, Position>;
  edges: Array<{ from: string; to: string }>;
  width: number;
  height: number;
}

const NODE_WIDTH = 180;
const NODE_HEIGHT = 52;
const H_GAP = 80;
const V_GAP = 20;
const PADDING = 40;

export default function DAGView({
  tasks,
  taskDeps,
  isLoading,
  onTaskClick,
  selectedTask,
}: DAGViewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(1);

  // Use dependencies from props (extracted from WASM meta by usePlanReasoning)
  const deps = taskDeps;

  // Calculate layout using topological sort
  const layout = useMemo((): LayoutResult => {
    const taskIds = tasks.map((t) => t.id);
    const positions = new Map<string, Position>();
    const edges: Array<{ from: string; to: string }> = [];

    // Build edge list
    for (const [taskId, taskDeps] of deps) {
      for (const dep of taskDeps) {
        if (taskIds.includes(dep)) {
          edges.push({ from: dep, to: taskId });
        }
      }
    }

    // Calculate depth (longest path from root)
    const depths = new Map<string, number>();
    const visited = new Set<string>();

    function calcDepth(taskId: string): number {
      if (depths.has(taskId)) return depths.get(taskId)!;
      if (visited.has(taskId)) return 0;
      visited.add(taskId);

      const taskDeps = deps.get(taskId) || [];
      if (taskDeps.length === 0) {
        depths.set(taskId, 0);
        return 0;
      }

      const maxDepDelta = Math.max(
        ...taskDeps.map((d) => (taskIds.includes(d) ? calcDepth(d) : -1))
      );
      const depth = maxDepDelta + 1;
      depths.set(taskId, depth);
      return depth;
    }

    for (const taskId of taskIds) {
      calcDepth(taskId);
    }

    // Group by depth
    const columns = new Map<number, string[]>();
    for (const taskId of taskIds) {
      const depth = depths.get(taskId) ?? 0;
      if (!columns.has(depth)) columns.set(depth, []);
      columns.get(depth)!.push(taskId);
    }

    // Sort within columns by status for visual grouping
    const statusOrder = { done: 0, in_progress: 1, ready: 2, blocked: 3, backlog: 4 };
    const taskMap = new Map(tasks.map((t) => [t.id, t]));

    for (const [, col] of columns) {
      col.sort((a, b) => {
        const ta = taskMap.get(a);
        const tb = taskMap.get(b);
        const sa = ta ? statusOrder[ta.status] : 5;
        const sb = tb ? statusOrder[tb.status] : 5;
        return sa - sb || a.localeCompare(b);
      });
    }

    // Assign positions
    const maxDepth = Math.max(...Array.from(depths.values()), 0);
    let maxColSize = 0;

    for (const [depth, taskIds] of columns) {
      maxColSize = Math.max(maxColSize, taskIds.length);
      const x = PADDING + depth * (NODE_WIDTH + H_GAP);
      taskIds.forEach((taskId, idx) => {
        const y = PADDING + idx * (NODE_HEIGHT + V_GAP);
        positions.set(taskId, { x, y });
      });
    }

    const width = (maxDepth + 1) * (NODE_WIDTH + H_GAP) + PADDING * 2;
    const height = maxColSize * (NODE_HEIGHT + V_GAP) + PADDING * 2;

    return { positions, edges, width, height };
  }, [tasks, deps]);

  // Zoom controls
  const zoomIn = useCallback(() => setZoom((z) => Math.min(z + 0.1, 2)), []);
  const zoomOut = useCallback(() => setZoom((z) => Math.max(z - 0.1, 0.5)), []);
  const resetZoom = useCallback(() => {
    setZoom(1);
    viewportRef.current?.scrollTo(0, 0);
  }, []);

  // Keyboard shortcuts for zoom
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "=" || e.key === "+") zoomIn();
      if (e.key === "-") zoomOut();
      if (e.key === "0") resetZoom();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [zoomIn, zoomOut, resetZoom]);

  const statusColors: Record<string, string> = {
    done: "hsl(var(--su) / 0.2)",
    in_progress: "hsl(var(--s) / 0.2)",
    ready: "hsl(var(--p) / 0.2)",
    blocked: "hsl(var(--wa) / 0.2)",
    backlog: "hsl(var(--b2))",
  };

  const statusBorders: Record<string, string> = {
    done: "hsl(var(--su) / 0.5)",
    in_progress: "hsl(var(--s) / 0.5)",
    ready: "hsl(var(--p) / 0.5)",
    blocked: "hsl(var(--wa) / 0.5)",
    backlog: "hsl(var(--b3))",
  };

  // Edge colors matching source task status
  const edgeColors: Record<string, string> = {
    done: "#22c55e",           // Green - matches Done status
    in_progress: "#6366f1",    // Indigo - matches In Progress status
    ready: "#3b82f6",          // Blue - matches Ready status
    blocked: "#f59e0b",        // Amber - matches Blocked status
    backlog: "#94a3b8",        // Slate - matches Backlog status
  };

  const taskMap = new Map(tasks.map((t) => [t.id, t]));

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Legend and controls */}
      <div className="flex items-center gap-2 sm:gap-4 px-2 sm:px-4 py-1.5 sm:py-2 bg-base-100 border-b border-base-300 flex-wrap text-[10px] sm:text-xs">
        <div className="font-medium text-base-content/50">Tasks:</div>
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-sm bg-success"></div>
          <span>Done</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-sm bg-secondary"></div>
          <span>In Progress</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-sm bg-primary"></div>
          <span>Ready</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-sm bg-warning"></div>
          <span>Blocked</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-sm bg-base-300"></div>
          <span>Backlog</span>
        </div>

        <div className="hidden sm:block w-px h-4 bg-base-300"></div>

        <div className="font-medium text-base-content/50">Edges:</div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-0.5 rounded" style={{ backgroundColor: edgeColors.done }}></div>
          <span>Done</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-0.5 rounded" style={{ backgroundColor: edgeColors.in_progress }}></div>
          <span>In Progress</span>
        </div>
        <div className="flex items-center gap-1">
          <svg className="w-4 h-2" viewBox="0 0 16 8">
            <line x1="0" y1="4" x2="16" y2="4" stroke={edgeColors.ready} strokeWidth="2" strokeDasharray="4 2" />
          </svg>
          <span>Ready</span>
        </div>
        <div className="flex items-center gap-1">
          <svg className="w-4 h-2" viewBox="0 0 16 8">
            <line x1="0" y1="4" x2="16" y2="4" stroke={edgeColors.blocked} strokeWidth="2" strokeDasharray="4 2" />
          </svg>
          <span>Blocked</span>
        </div>
        <div className="flex items-center gap-1">
          <svg className="w-4 h-2" viewBox="0 0 16 8">
            <line x1="0" y1="4" x2="16" y2="4" stroke={edgeColors.backlog} strokeWidth="2" strokeDasharray="4 2" />
          </svg>
          <span>Backlog</span>
        </div>
        <div className="flex-1"></div>

        {/* Zoom controls */}
        <div className="join">
          <button className="join-item btn btn-xs" onClick={zoomOut}>-</button>
          <span className="join-item btn btn-xs btn-ghost">{Math.round(zoom * 100)}%</span>
          <button className="join-item btn btn-xs" onClick={zoomIn}>+</button>
          <button className="join-item btn btn-xs" onClick={resetZoom}>Fit</button>
        </div>
      </div>

      {/* DAG viewport */}
      <div ref={viewportRef} className="dag-viewport flex-1 overflow-auto bg-base-200 relative">
        <div
          ref={containerRef}
          className="relative"
          style={{
            width: layout.width,
            height: layout.height,
            transform: `scale(${zoom})`,
            transformOrigin: "top left",
          }}
        >
          {/* SVG edges */}
          <svg
            className="absolute top-0 left-0 pointer-events-none"
            width={layout.width}
            height={layout.height}
          >
            {layout.edges.map((edge, i) => {
              const fromPos = layout.positions.get(edge.from);
              const toPos = layout.positions.get(edge.to);
              if (!fromPos || !toPos) return null;

              const fromTask = taskMap.get(edge.from);

              // Edge color based on source task status
              const edgeColor = edgeColors[fromTask?.status || "backlog"] || edgeColors.backlog;

              // Solid lines for completed/active, dashed for pending work
              const isDashed = fromTask?.status !== "done" && fromTask?.status !== "in_progress";

              // Edge endpoints
              const x1 = fromPos.x + NODE_WIDTH;
              const y1 = fromPos.y + NODE_HEIGHT / 2;
              const x2 = toPos.x;
              const y2 = toPos.y + NODE_HEIGHT / 2;

              // Bezier control points
              const dx = x2 - x1;
              const cp1x = x1 + dx * 0.4;
              const cp2x = x1 + dx * 0.6;

              const isHighlighted =
                selectedTask === edge.from || selectedTask === edge.to;
              const opacity = isHighlighted ? 1 : 0.7;
              const strokeWidth = isHighlighted ? 2.5 : 2;

              return (
                <g key={i}>
                  <path
                    d={`M ${x1} ${y1} C ${cp1x} ${y1}, ${cp2x} ${y2}, ${x2} ${y2}`}
                    fill="none"
                    stroke={edgeColor}
                    strokeWidth={strokeWidth}
                    opacity={opacity}
                    strokeDasharray={isDashed ? "6 4" : undefined}
                  />
                  {/* Arrow */}
                  <polygon
                    points={`${x2},${y2} ${x2 - 8},${y2 - 4} ${x2 - 8},${y2 + 4}`}
                    fill={edgeColor}
                    opacity={opacity}
                  />
                </g>
              );
            })}
          </svg>

          {/* Task nodes */}
          {tasks.map((task) => {
            const pos = layout.positions.get(task.id);
            if (!pos) return null;

            const isSelected = selectedTask === task.id;

            return (
              <div
                key={task.id}
                className={`absolute cursor-pointer transition-transform hover:scale-[1.02] hover:z-10 ${
                  isSelected ? "ring-2 ring-primary ring-offset-2" : ""
                }`}
                style={{
                  left: pos.x,
                  top: pos.y,
                  width: NODE_WIDTH,
                }}
                onClick={() => onTaskClick?.(task.id)}
              >
                <div
                  className="card border shadow-sm h-full bg-base-100"
                  style={{
                    borderColor: statusBorders[task.status] || statusBorders.backlog,
                    borderLeftWidth: 4,
                    borderLeftColor: statusBorders[task.status] || statusBorders.backlog,
                  }}
                >
                  <div className="card-body p-2">
                    <div className="flex justify-between items-start gap-1">
                      <span
                        className="font-medium text-xs truncate flex-1"
                        title={task.id}
                      >
                        {task.id}
                      </span>
                      {task.status === "done" && (
                        <svg
                          className="w-3 h-3 text-success shrink-0"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                      {task.status === "in_progress" && (
                        <span className="loading loading-spinner loading-xs text-secondary shrink-0"></span>
                      )}
                      {task.status === "ready" && (
                        <span className="badge badge-primary badge-xs shrink-0">+d</span>
                      )}
                      {task.status === "blocked" && (
                        <span className="badge badge-warning badge-xs shrink-0">-d</span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 mt-1">
                      <span
                        className={`badge badge-xs ${
                          task.status === "done"
                            ? "badge-success"
                            : task.status === "in_progress"
                            ? "badge-secondary"
                            : task.status === "ready"
                            ? "badge-primary"
                            : task.status === "blocked"
                            ? "badge-warning"
                            : "badge-ghost"
                        }`}
                      >
                        {task.status.replace("_", " ")}
                      </span>
                      {task.agentType && (
                        <span className="badge badge-outline badge-xs">
                          {task.agentType}
                        </span>
                      )}
                      {task.agent && task.status === "in_progress" && (
                        <Avatar name={task.agent} keyHash={task.agentKeyHash} size="xs" />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
